import { notificationHref } from "@/lib/task-notification-link";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOrganization } from "./useOrganization";
import { useCurrentPerson } from "./useCurrentPerson";
import {
  fetchNotifications,
  fetchUnreadNotificationsCount,
  markAllNotificationsRead,
  NotificationServiceError,
  setNotificationReadStatus,
  setNotificationsReadStatus,
  type NotificationListFilters,
  type NotificationRecord,
  type PaginatedNotifications,
} from "@/api/notifications";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ACTION_TOAST_DURATION_MS } from "@/lib/toasts";
import { useNotificationPreferences } from "./useNotificationPreferences";
import { useDesktopNotifications } from "./useDesktopNotifications";
import { playNotificationSound } from "@/lib/notificationSound";

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────
/**
 * Fallback polling interval (60s) — only used when Realtime is degraded.
 * With Broadcast + postgres_changes, updates are near-instant, so the long
 * interval is just a safety net.
 */
const REALTIME_FALLBACK_POLL_MS = 60_000;

/**
 * Debounce window for batching rapid-fire Realtime events into a single
 * query invalidation.
 */
const INVALIDATION_DEBOUNCE_MS = 300;

// ────────────────────────────────────────────────────────────────────────────
// Core data hooks
// ────────────────────────────────────────────────────────────────────────────

export interface UseNotificationsOptions {
  /** Filtered in the query, so the page and its totals cover the filtered list. */
  filters?: NotificationListFilters;
  /** Defaults to true; the query also waits for the organization and the viewer's person. */
  enabled?: boolean;
}

/**
 * Hook to fetch paginated notifications for the current user.
 *
 * A load that failed is an error, never an empty page (#6552): fetchNotifications degrades a
 * retryable failure to an empty payload (`degraded`), which this hook rethrows so the list
 * shows its error state instead of "You're all caught up".
 */
export function useNotifications(page = 1, pageSize = 10, options: UseNotificationsOptions = {}) {
  const { organizationId } = useOrganization();
  const { data: person } = useCurrentPerson();
  const isReady = Boolean(organizationId && person?.id);
  const { filters, enabled = true } = options;
  const hasFilters = filters?.isRead !== undefined || Boolean(filters?.notificationType);

  return useQuery<PaginatedNotifications>({
    // Filters go last and only when set, so unfiltered callers keep their cache key and every
    // variant stays under the ["notifications", org, person] prefix the invalidations use.
    queryKey: [
      "notifications",
      organizationId,
      person?.id,
      page,
      pageSize,
      ...(hasFilters
        ? [{ isRead: filters?.isRead ?? null, notificationType: filters?.notificationType ?? null }]
        : []),
    ],
    queryFn: async () => {
      if (!organizationId || !person?.id) {
        return {
          data: [],
          totalCount: 0,
          page,
          pageSize,
          totalPages: 0,
        };
      }
      const result = await fetchNotifications({
        organizationId,
        personId: person.id,
        page,
        pageSize,
        filters: hasFilters ? filters : undefined,
      });
      if (result.degraded) {
        throw new NotificationServiceError(
          result.errorCode ?? "NOTIFICATIONS_DB_UNAVAILABLE",
          "Notifications couldn't be loaded",
          undefined,
          true,
        );
      }
      return result;
    },
    enabled: isReady && enabled,
    // Form alerts use REST only; poll active observers for eventual visibility.
    refetchInterval: REALTIME_FALLBACK_POLL_MS,
  });
}

/**
 * Hook to fetch unread notifications count for the current user.
 *
 * **Realtime-first**: The 60s poll is a fallback only. Instant updates
 * come from the Broadcast + postgres_changes channel in
 * useNotificationSubscription().
 */
export function useUnreadCount() {
  const { organizationId } = useOrganization();
  const { data: person } = useCurrentPerson();
  const isReady = Boolean(organizationId && person?.id);

  return useQuery<number>({
    queryKey: ["notifications-unread-count", organizationId, person?.id],
    queryFn: async () => {
      if (!organizationId || !person?.id) {
        return 0;
      }
      return fetchUnreadNotificationsCount({
        organizationId,
        personId: person.id,
      });
    },
    enabled: isReady,
    // Long fallback poll — Broadcast handles instant updates
    refetchInterval: REALTIME_FALLBACK_POLL_MS,
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Mutation hooks
// ────────────────────────────────────────────────────────────────────────────

/**
 * Hook to mark a notification as read or unread
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const { data: person } = useCurrentPerson();

  return useMutation({
    mutationFn: async (params: { notificationId: string; isRead: boolean }) => {
      if (!organizationId) {
        throw new Error("Missing organization for notification update");
      }
      return setNotificationReadStatus({ ...params, organizationId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", organizationId, person?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-unread-count", organizationId, person?.id],
      });
    },
    onError: (error: Error) => {
      console.error("Failed to mark notification as read:", error);
      toast.error("Failed to mark notification as read");
    },
  });
}

/**
 * Marks several notifications read or unread in one update: the list's bulk "Mark as read"
 * and its Undo. Errors are left to the caller, which reports the whole batch once.
 */
export function useSetNotificationsReadStatus() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const { data: person } = useCurrentPerson();

  return useMutation({
    mutationFn: async (params: { notificationIds: string[]; isRead: boolean }) => {
      if (!organizationId) {
        throw new Error("Missing organization for notification update");
      }
      return setNotificationsReadStatus({ ...params, organizationId });
    },
    onSettled: () => {
      // Settled, not only success: an update that got no response may still have landed.
      queryClient.invalidateQueries({
        queryKey: ["notifications", organizationId, person?.id],
      });
      // The bell's list (useRecentNotifications, any limit), including its fallback poll.
      queryClient.invalidateQueries({
        queryKey: ["notifications-recent", organizationId, person?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-unread-count", organizationId, person?.id],
      });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const { data: person } = useCurrentPerson();

  return useMutation({
    mutationFn: async () => {
      if (!organizationId || !person?.id) {
        throw new Error("Missing organization or person for notifications.");
      }
      return markAllNotificationsRead({
        organizationId,
        personId: person.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", organizationId, person?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-unread-count", organizationId, person?.id],
      });
    },
    onError: (error: Error) => {
      console.error("Failed to mark all notifications as read:", error);
      toast.error("Failed to mark all notifications as read");
    },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Realtime subscription — Broadcast + postgres_changes (Phase 2A upgrade)
// ────────────────────────────────────────────────────────────────────────────

type NotificationSubscriptionOptions = {
  showDesktopNotifications?: boolean;
};

/**
 * Combined Realtime subscription for notification updates.
 *
 * Uses **two delivery mechanisms** on a single channel:
 *
 * 1. **Broadcast** — The server-side notification creator (edge function or
 *    DB trigger) sends a lightweight Broadcast event to the per-person
 *    channel. This is near-instant (<100ms) with no Realtime replication
 *    lag.  The payload is small (just the notification summary) so the
 *    client can show a toast immediately and invalidate queries in the
 *    background.
 *
 * 2. **postgres_changes** — As a fallback, we still listen for INSERT/UPDATE
 *    on the `notifications` table filtered by person_id. This catches any
 *    notifications that didn't send a Broadcast event (e.g., bulk imports,
 *    background jobs) and ensures eventual consistency.
 *
 * Both mechanisms share a debounced invalidation callback so rapid-fire
 * events don't trigger a query storm.
 */
export function useNotificationSubscription(
  options: NotificationSubscriptionOptions = {}
) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const { data: person } = useCurrentPerson();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const { shouldShowToastNotification, shouldPlaySoundNotification, shouldShowDesktopNotification } =
    useNotificationPreferences();
  const { isSupported, permission, showNotificationFromRecord } = useDesktopNotifications();
  // Defaults to false: multiple components mount this hook at once (the
  // persistent NotificationCenter and the /notifications page), and without
  // cross-instance dedup, every mounted instance would independently fire a
  // browser notification for the same row. Only the explicitly designated
  // persistent subscriber should opt in.
  const wantsDesktopNotifications = options.showDesktopNotifications === true;

  // Debounced query invalidation — batches rapid events
  const invalidateNotificationQueries = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", organizationId, person?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-recent", organizationId, person?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["notifications-unread-count", organizationId, person?.id],
      });
    }, INVALIDATION_DEBOUNCE_MS);
  }, [queryClient, organizationId, person?.id]);

  useEffect(() => {
    if (!organizationId || !person?.id) return;

    const channelName = `notifications:${person.id}`;

    const channel = supabase
      .channel(channelName)
      // ─── Broadcast listener (near-instant) ────────────────────────
      .on("broadcast", { event: "new_notification" }, (payload) => {
        const data = payload.payload as {
          id?: string;
          title?: string;
          body?: string;
          link_url?: string;
          link_label?: string;
          type?: string;
        };

        const notificationType = data.type ?? null;

        // Instant toast. The stable id is what dedupes this against the
        // postgres_changes INSERT fallback below — without it the same
        // notification stacks twice (broadcast first, pg_changes 1-2s later).
        if (data.title) {
          if (shouldShowToastNotification(notificationType)) {
            toast(data.title, {
              description: data.body || undefined,
              // Toasts with a "View" action need time to be reached (UX 2.4).
              duration: data.link_url ? ACTION_TOAST_DURATION_MS : undefined,
              action: data.link_url
                ? {
                    label: data.link_label || "View",
                    onClick: () => {
                      window.location.href = notificationHref({ link_url: data.link_url!, organization_id: organizationId })!;
                    },
                  }
                : undefined,
              id: data.id ? `notification-${data.id}` : undefined,
            });
          }

          // Gated independently of the toast preference — a user who
          // disabled toasts but enabled sound separately must still hear it.
          if (shouldPlaySoundNotification(notificationType)) {
            playNotificationSound(data.id);
          }
        }

        invalidateNotificationQueries();
      })
      // ─── Broadcast: read status changed ───────────────────────────
      .on("broadcast", { event: "notification_read" }, () => {
        invalidateNotificationQueries();
      })
      // ─── postgres_changes INSERT fallback ─────────────────────────
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `person_id=eq.${person.id}`,
        },
        (payload) => {
          const notification = payload.new as NotificationRecord;

          // Only show toast if we didn't already get it via Broadcast
          // (Broadcast arrives first; postgres_changes may lag by 1-2s)
          if (shouldShowToastNotification(notification.notification_type)) {
            toast(notification.title, {
              description: notification.body || undefined,
              // Toasts with a "View" action need time to be reached (UX 2.4).
              duration: notification.link_url ? ACTION_TOAST_DURATION_MS : undefined,
              action: notification.link_url
                ? {
                    label: notification.link_label || "View",
                    onClick: () => {
                      window.location.href = notificationHref(notification)!;
                    },
                  }
                : undefined,
              id: `notification-${notification.id}`, // deduplicate with sonner
            });
          }

          // Gated independently of the toast preference — a user who
          // disabled toasts but enabled sound separately must still hear it.
          if (shouldPlaySoundNotification(notification.notification_type)) {
            playNotificationSound(notification.id);
          }

          if (
            wantsDesktopNotifications &&
            isSupported &&
            permission === "granted" &&
            shouldShowDesktopNotification(notification.notification_type)
          ) {
            showNotificationFromRecord(notification);
          }

          invalidateNotificationQueries();
        }
      )
      // ─── postgres_changes UPDATE fallback ─────────────────────────
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `person_id=eq.${person.id}`,
        },
        () => {
          invalidateNotificationQueries();
        }
      )
      .subscribe((status) => {
        setRealtimeConnected(status === "SUBSCRIBED");
        if (status === "SUBSCRIBED") {
          console.debug("[Notifications] Realtime channel subscribed:", channelName);
        }
        if (status === "CHANNEL_ERROR") {
          console.warn("[Notifications] Realtime channel error — falling back to polling");
        }
      });

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [
    organizationId,
    person?.id,
    queryClient,
    invalidateNotificationQueries,
    shouldPlaySoundNotification,
    shouldShowToastNotification,
    shouldShowDesktopNotification,
    wantsDesktopNotifications,
    isSupported,
    permission,
    showNotificationFromRecord,
  ]);

  return { realtimeConnected };
}

// ────────────────────────────────────────────────────────────────────────────
// Recent notifications (for popover)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Fetch recent notifications (for popover display)
 */
export function useRecentNotifications(limit = 5) {
  const { organizationId } = useOrganization();
  const { data: person } = useCurrentPerson();
  const isReady = Boolean(organizationId && person?.id);

  return useQuery<NotificationRecord[]>({
    queryKey: ["notifications-recent", organizationId, person?.id, limit],
    queryFn: async () => {
      if (!organizationId || !person?.id) {
        return [];
      }
      const result = await fetchNotifications({
        organizationId,
        personId: person.id,
        page: 1,
        pageSize: limit,
      });
      return result.data;
    },
    enabled: isReady,
    // Form alerts use REST only; poll active observers for eventual visibility.
    refetchInterval: REALTIME_FALLBACK_POLL_MS,
  });
}
